import Highcharts, { Options } from 'highcharts';
import noDataModule from 'highcharts/modules/no-data-to-display';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TooltipOptionsExtended } from '../types/tooltip-data';
import '../styles/CompanyGraphs.scss';
import '../extensions/highcharts-plugins';
import { black, blue, GRAPH_FONT_SIZE, grey } from '../constants/company-chart';
import { createComparableTimeDataSeries, createDataFromTimerangeForCompanies } from '../utils/compare-time-functions';
import { breakPoints, scoreBucketName } from '../constants/downgrade-probability';
import { formatDateString, formatYYYYMMDDForLocale } from '../utils/dates';
import { useRecoilState, useRecoilValue } from 'recoil';
import { customID, customRange, pageDateRangeAtom, pageDateRangeOptions } from '../store/timerange';
import { getFeatureFlags } from '../utils/config';
import { companyScoreHistoryAtom } from '../store/company';
import useChartTitle from '../hooks/use-chart-title';
import { repositionTicks } from '../utils/company-chart-functions';
import { formatCompanyData } from '../utils/company-data-formatter';

noDataModule(Highcharts);

export default function CompareTime({
  list,
}: Readonly<{
  list?: string;
}>) {
  const ref = useRef<any>();
  const chartRef = useRef(null);
  const [pageDateRange, setPageDateRange] = useRecoilState(pageDateRangeAtom);
  const chartTitle = useChartTitle();
  const [yMinMax, setYMinMax] = useState({ min: 0, max: 1 });

  const companyScoreHistory = useRecoilValue(companyScoreHistoryAtom);

  const dataFromTimeRangeForCompanies = useMemo(() => {
    const formattedData = companyScoreHistory.map((company) => formatCompanyData(company, pageDateRange));
    return createDataFromTimerangeForCompanies(formattedData, pageDateRange);
  }, [companyScoreHistory, pageDateRange]);

  const firstDataFromTimeRange = useMemo(() => {
    return dataFromTimeRangeForCompanies.length > 0 ? dataFromTimeRangeForCompanies[0] : null;
  }, [dataFromTimeRangeForCompanies]);

  const tooltipOptions: TooltipOptionsExtended = {
    useHTML: true,
    borderWidth: 0,
    backgroundColor: 'rgba(255,255,255,0)',
    shadow: false,
    // charts crash without this function
    remainOnMouseout: function () {
      return true;
    },
  };

  const defaultPlotLineLabelOptions = {
    y: 20,
    style: { color: '#949494' }, // ow-grey
  };

  const [chartKey, setChartKey] = useState(0);

  const filterDataByDate = async (minDate: string, maxDate: string) => {
    setPageDateRange({ value: customID, label: customRange, earliestDate: minDate, latestDate: maxDate });
    setChartKey((prevKey) => prevKey + 1);
  };

  const options: Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        animation: true,
        backgroundColor: 'transparent',
        height: 650,
        spacing: [10, 150, 10, 0],
        zooming: {
          type: 'xy',
        },
        resetZoomButton: {
          theme: {
            style: {
              display: 'none',
            },
          },
        },
        events: {
          selection: function (e) {
            const yMin = e.yAxis[0].min;
            const yMax = e.yAxis[0].max;
            setYMinMax({ min: yMin, max: yMax });

            const xMin = e.xAxis[0].min;
            const xMax = e.xAxis[0].max;
            const xAxis = this.xAxis[0];

            const categories = xAxis.categories;
            if (categories) {
              const minCategoryIndex = Math.floor(xMin);
              const maxCategoryIndex = Math.ceil(xMax);

              const minCategory = categories[minCategoryIndex];
              const maxCategory = categories[maxCategoryIndex];

              if (minCategory && maxCategory) {
                filterDataByDate(minCategory, maxCategory);
              }
            }

            return true;
          },
        },
      },
      credits: {
        enabled: false,
      },
      lang: {
        noData: '<div style="color:#FF0000">No companies match these filter criteria in your portfolio.</div>',
      },
      legend: {
        enabled: false,
      },
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      noData: {
        useHTML: true,
      },
      series: createComparableTimeDataSeries(dataFromTimeRangeForCompanies),
      title: { text: '' },
      tooltip: tooltipOptions,
      plotOptions: {
        spline: {
          cursor: 'pointer',
          marker: {
            symbol: 'circle',
          },
          events: {
            click: function (s: any) {
              const companyCode = s.point.series.userOptions.custom.companyCode;
              // eslint-disable-next-line no-restricted-globals
              location.pathname = `company/${companyCode}`;
            },
          },
          tooltip: {
            headerFormat: '',
            pointFormatter: function () {
              return `
              <div style="background: #fff;border: 1px solid #dadada;min-width: 140px; border-radius: 10px;z-index: 20;">
                <div style="color:#FFF;background: black;padding: 6px 10px 2px 10px;text-align: center;border-top-left-radius: 10px;border-top-right-radius: 10px;font-weight:bold;">
                  ${this.series.name}
                </div>
                <div style="color:#FFF;background: black;padding: 2px 10px 5px 10px;text-align: center;">
                  ${formatYYYYMMDDForLocale(firstDataFromTimeRange?.xAxisCategories[this.x] as string)}
                </div>
                <div style="display:flex; justify-content:center;padding:10px;">
                  <table >
                    <tr>
                      <td style="text-align: right">FSS<br> Score</td>
                      <td style="padding-left: 10px; color:${this.color}; font-size: 20px;font-weight: 700;line-height: 18px;text-align:right;">${this.y && Math.round(this.y * 100)}</td>
                    </tr>
                  </table>
                </div>
              </div>`;
            },
          },
        },
        series: {
          dataLabels: {
            enabled: false,
            crop: false,
            overflow: 'allow',
            align: 'left',
            verticalAlign: 'middle',
            y: 0,
            x: 10,
            allowOverlap: true,
            style: {
              textOverflow: 'clip',
              width: 140,
            },
            formatter: function () {
              return this.series.name;
            },
          },
        },
      },
      yAxis: [
        {
          title: {
            text: 'FSS Score',
            style: { color: black, fontSize: GRAPH_FONT_SIZE },
          },
          labels: {
            style: { color: black, fontSize: GRAPH_FONT_SIZE },
            formatter: function () {
              const label = this.axis.defaultLabelFormatter.call(this);
              const value = parseFloat(label);
              return `${value * 100}`;
            },
          },
          min: yMinMax.min,
          max: yMinMax.max,
          tickInterval: yMinMax.max - yMinMax.min < 0.5 ? 0.1 : 0.2,
          lineColor: blue,
          lineWidth: 4,
          plotLines: [
            { color: '#DADADA', value: breakPoints[1] / 100, label: { text: scoreBucketName[1], ...defaultPlotLineLabelOptions } },
            { color: '#DADADA', value: breakPoints[2] / 100, label: { text: scoreBucketName[2], ...defaultPlotLineLabelOptions } },
            { color: '#DADADA', value: breakPoints[3] / 100, label: { text: scoreBucketName[3], ...defaultPlotLineLabelOptions } },
            { color: '#DADADA', value: breakPoints[4] / 100, label: { text: scoreBucketName[4], ...defaultPlotLineLabelOptions } },
            { color: 'transparent', value: 1, label: { text: scoreBucketName[5], ...defaultPlotLineLabelOptions } },
          ], // 'ow-light-grey': ,
          gridLineWidth: 0,
        },
      ],
      ...(firstDataFromTimeRange && {
        xAxis: {
          categories: firstDataFromTimeRange.xAxisCategories,
          title: { text: 'Time', margin: 15, style: { color: black, fontSize: GRAPH_FONT_SIZE } },
          lineColor: grey,
          lineWidth: 1,
          tickPositioner: function () {
            return repositionTicks(this, firstDataFromTimeRange);
          },
          tickmarkPlacement: 'on',
          tickWidth: 1,
          maxPadding: 0,
          min: 0.5,
          max: firstDataFromTimeRange.xAxisCategories.length - 1.5,
          tickColor: grey,
          labels: {
            style: {
              color: black,
              fontSize: GRAPH_FONT_SIZE,
            },
            formatter: function () {
              const label = this.axis.defaultLabelFormatter.call(this);
              return formatDateString(label, firstDataFromTimeRange.daysInOneTick);
            },
          },
        },
      }),
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 1024,
            },
            chartOptions: {
              chart: {
                spacing: [10, 0, 0, 0],
              },
            },
          },
        ],
      },
      exporting: {
        fallbackToExportServer: false,
        allowHTML: true,
        enabled: true,
        chartOptions: {
          chart: {
            height: 650,
            backgroundColor: 'white',
          },
          tooltip: { enabled: false },
          title: {
            useHTML: true,
            text: chartTitle.formatChartTitle(!!list),
            align: 'center',
          },
        },
        scale: 2,
        sourceWidth: 1360,
        sourceHeight: 650,
        filename: `chart-time-${list || 'Portfolio'}`,
        buttons: {
          contextButton: {
            enabled: false,
          },
        },
      },
    }),
    [dataFromTimeRangeForCompanies, firstDataFromTimeRange]
  );

  const isDownloadChartEnabled = getFeatureFlags().has('download_chart');

  const exportChartAsPNG = () => {
    const chart = (chartRef.current as any)?.chart;
    chart.exportChartLocal();
  };

  const resetDateRange = () => {
    setPageDateRange(pageDateRangeOptions[0]);
  };

  const resetZoom = () => {
    resetDateRange();
    setYMinMax({ min: 0, max: 1 });
  };

  // On mount
  useEffect(() => {
    resetDateRange();

    // On unmount
    return () => {
      resetDateRange();
    };
  }, []);

  return (
    <div ref={ref}>
      <div className="flex justify-end items-center mb-4">
        {isDownloadChartEnabled && (
          <button onClick={exportChartAsPNG} className="fss-chart-button" title="Download chart">
            <img src="/icons/download.svg" alt="Download chart" />
            <div>Download chart</div>
          </button>
        )}
        <div className="fss-button-spacer"></div>
        <button onClick={resetZoom} className="fss-chart-button" title="Reset zoom level">
          <img src="/icons/reset.svg" alt="Reset zoom level" />
          <div>Zoom level</div>
        </button>
      </div>

      <HighchartsReact key={chartKey} ref={chartRef} highcharts={Highcharts} options={options} />
    </div>
  );
}
